import React, {useEffect} from 'react'
import { convertFromRaw, Editor, EditorState, getDefaultKeyBinding, RichUtils } from 'draft-js'
import {
  BoldOutlined,
  ItalicOutlined,
  OrderedListOutlined,
  UnderlineOutlined,
  UnorderedListOutlined,
  VerticalLeftOutlined
} from '@ant-design/icons'
import '../../asset/less/RichTextEditor.css'
import 'draft-js/dist/Draft.css'
import { Button } from 'antd'

/**
 * Copyright (c) Facebook, Inc. and its affiliates. All rights reserved.
 *
 * This file provided by Facebook is for non-commercial testing and evaluation
 * purposes only. Facebook reserves all rights not expressly granted.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * FACEBOOK BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN
 * ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
 * CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

function Observer({ value, didUpdate }) {
  useEffect(() => {
    didUpdate(value)
  }, [value])
  return null // component does not render anything
}

class RichTextEditor extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      //editorState: EditorState.createWithContent(
      //ContentState.createFromText(this.props.default))
      editorState: EditorState.createEmpty()
    }

    this.focus = () => this.refs.editor.focus()
    this.onChange = (editorState) => {
      this.setState({ editorState })
      //this.props.onChange(editorState)
    }

    this.handleKeyCommand = this._handleKeyCommand.bind(this)
    this.mapKeyToEditorCommand = this._mapKeyToEditorCommand.bind(this)
    this.toggleBlockType = this._toggleBlockType.bind(this)
    this.toggleInlineStyle = this._toggleInlineStyle.bind(this)
  }

  _handleKeyCommand(command, editorState) {
    const newState = RichUtils.handleKeyCommand(editorState, command)
    if (newState) {
      this.onChange(newState)
      return true
    }
    return false
  }

  _mapKeyToEditorCommand(e) {
    if (e.keyCode === 9 /* TAB */) {
      const newEditorState = RichUtils.onTab(e, this.state.editorState, 4 /* maxDepth */)
      if (newEditorState !== this.state.editorState) {
        this.onChange(newEditorState)
      }
      return
    }
    return getDefaultKeyBinding(e)
  }

  _toggleBlockType(blockType) {
    this.onChange(RichUtils.toggleBlockType(this.state.editorState, blockType))
  }

  _toggleInlineStyle(inlineStyle) {
    this.onChange(RichUtils.toggleInlineStyle(this.state.editorState, inlineStyle))
  }

  loadCurr() {
    //need to destringify, then turn that object to ContentState to push
    const body = JSON.parse(this.props.toLoad)
    const loadedContent = convertFromRaw(body)
    const newEditorState = EditorState.push(this.state.editorState, loadedContent)
    this.setState({ editorState: newEditorState })
  }

  render() {
    const { editorState } = this.state

    // If the user changes block type before entering any text, we can
    // either style the placeholder or hide it. Let's just hide it now.
    let className = 'RichEditor-editor'
    var contentState = editorState.getCurrentContent()
    if (!contentState.hasText()) {
      if (contentState.getBlockMap().first().getType() !== 'unstyled') {
        className += ' RichEditor-hidePlaceholder'
      }
    }

    return (
      <div>
        <div className='RichEditor-root' style={this.props.style ?? {}}>
          <InlineStyleControls editorState={editorState} onToggle={this.toggleInlineStyle} />
          <BlockStyleControls editorState={editorState} onToggle={this.toggleBlockType} />
          <div className={className} onClick={this.focus}>
            <Editor
              blockStyleFn={getBlockStyle}
              customStyleMap={styleMap}
              editorState={editorState}
              handleKeyCommand={this.handleKeyCommand}
              keyBindingFn={this.mapKeyToEditorCommand}
              onChange={this.onChange}
              //placeholder="Tell your story..."
              ref='editor'
              spellCheck={true}
            />
          </div>
        </div>
      </div>
    )
  }
}

// Custom overrides for "code" style.
const styleMap = {
  CODE: {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    fontFamily: '"Inconsolata", "Menlo", "Consolas", monospace',
    fontSize: 16,
    padding: 2
  }
}

function getBlockStyle(block) {
  switch (block.getType()) {
    case 'blockquote':
      return 'RichEditor-blockquote'
    default:
      return null
  }
}

class StyleButton extends React.Component {
  constructor() {
    super()
    this.onToggle = (e) => {
      e.preventDefault()
      this.props.onToggle(this.props.style)
    }
  }

  render() {
    let className = 'RichEditor-styleButton'
    if (this.props.active) {
      if (this.props.label === 'Bold' || this.props.label === 'Italic' || this.props.label === 'Underline') {
        className += ' RichEditor-activeStyleButton'
      } else {
        className += ' RichEditor-activeButton'
      }
    }
    if (this.props.label === 'Bold') {
      return (
        <span className={className} onMouseDown={this.onToggle}>
          <Button size='small' icon={<BoldOutlined />} />
        </span>
      )
    } else if (this.props.label === 'Italic') {
      return (
        <span className={className} onMouseDown={this.onToggle}>
          <Button size='small' icon={<ItalicOutlined />} />
        </span>
      )
    } else if (this.props.label === 'Underline') {
      return (
        <span className={className} onMouseDown={this.onToggle}>
          <Button size='small' icon={<UnderlineOutlined />} />
        </span>
      )
    } else if (this.props.label === 'Blockquote') {
      return (
        <span className={className} onMouseDown={this.onToggle}>
          <Button size='small' icon={<VerticalLeftOutlined />} />
        </span>
      )
    } else if (this.props.label === 'UnorderedList') {
      return (
        <span className={className} onMouseDown={this.onToggle}>
          <Button size='small' icon={<UnorderedListOutlined />} />
        </span>
      )
    } else if (this.props.label === 'OrderedList') {
      return (
        <span className={className} onMouseDown={this.onToggle}>
          <Button size='small' icon={<OrderedListOutlined />} />
        </span>
      )
    } else {
      return (
        <span className={className} onMouseDown={this.onToggle}>
          {this.props.label}
        </span>
      )
    }
  }
}

const BLOCK_TYPES = [
  {label: 'Blockquote', style: 'blockquote'},
  { label: 'UnorderedList', style: 'unordered-list-item' },
  { label: 'OrderedList', style: 'ordered-list-item' },
  { label: 'H1', style: 'header-one' },
  { label: 'H2', style: 'header-two' },
  {label: 'H3', style: 'header-three'},
  {label: 'H4', style: 'header-four'},
  { label: 'H5', style: 'header-five' },
  {label: 'H6', style: 'header-six'},
  //{label: 'Code Block', style: 'code-block'},
]

const BlockStyleControls = (props) => {
  const { editorState } = props
  const selection = editorState.getSelection()
  const blockType = editorState.getCurrentContent().getBlockForKey(selection.getStartKey()).getType()

  return (
    <div
      className='RichEditor-controls'
      style={{
        display: 'inline-block'
      }}
    >
      {BLOCK_TYPES.map((type) => (
        <StyleButton key={type.label} active={type.style === blockType} label={type.label} onToggle={props.onToggle} style={type.style} />
      ))}
    </div>
  )
}

var INLINE_STYLES = [
  { label: 'Bold', style: 'BOLD' },
  { label: 'Italic', style: 'ITALIC' },
  { label: 'Underline', style: 'UNDERLINE' }
  //{label: 'Monospace', style: 'CODE'},
]

const InlineStyleControls = (props) => {
  const currentStyle = props.editorState.getCurrentInlineStyle()

  return (
    <div
      className='RichEditor-controls'
      style={{
        display: 'inline-block'
      }}
    >
      {INLINE_STYLES.map((type) => (
        <StyleButton
          key={type.label}
          active={currentStyle.has(type.style)}
          label={type.label}
          onToggle={props.onToggle}
          style={type.style}
        />
      ))}
    </div>
  )
}

export default RichTextEditor
